<template>
    <div class="vx-statistic-badge">
        <div
            :class="{ 'stat-link': hasLink, 'right': alignRight }"
            class="flex items-center align-middle relative h-12"
            @click="followLink"
            @mouseover="hovered = true"
            @mouseleave="hovered = false"
        >
            <template v-if="alignRight">
                <div class="stat-text">
                    <div class="w-full">
                        <h4 class="font-bold">{{ value }}</h4>
                        <span>{{ name }}</span>
                    </div>
                </div>
                <div class="icon-avatar">
                    <vx-basic-icon
                        v-if="!hideIcon"
                        rounded
                        :size="size"
                        :icon="icon_x"
                        :color="color"
                        :useMdi="useMdi"
                    />
                </div>
            </template>
            <template v-else>
                <span
                    v-if="hasLink"
                    class="go-text text-2xl absolute font-bold"
                    :style="{
                        color: color_x
                    }"
                >
                    Go
                </span>
                <div class="icon-avatar">
                    <div
                        v-if="hasLink"
                        class="filler"
                        :style="{ 'background-color': backgroundColor_x }"
                    />
                    <vx-basic-icon
                        v-if="!hideIcon"
                        rounded
                        :size="size"
                        :icon="icon_x"
                        :color="color"
                        :useMdi="useMdi"
                    />
                </div>
                <div class="stat-text" :class="{ 'ml-12': hasLink }">
                    <div class="w-full">
                        <h4 class="font-bold">{{ value }}</h4>
                        <span>{{ name }}</span>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import _color from '@/assets/utils/color.js'

export default {
    name: 'vx-statistic-badge',
    props: {
        name: {
            type: String,
            required: true
        },
        value: {
            type: [String, Number],
            required: true
        },
        color: {
            type: String,
            default: 'primary'
        },
        icon: {
            type: String
        },
        link: {
            type: [String, Object]
        },
        beforeLink: {
            type: Function
        },
        alignRight: {
            type: Boolean,
            default: false
        },
        useMdi: {
            type: Boolean,
            default: false
        },
        size: {
            type: [String, Number],
            default: 12
        }
    },
    data() {
        return {
            hovered: false
        }
    },
    computed: {
        icon_x() {
            if (!this.hovered || !this.hasLink) {
                return this.icon
            }
            if (this.alignRight) {
                return 'ChevronLeftIcon'
            }
            return 'ChevronRightIcon'
        },
        hasLink() {
            return !!this.link
        },
        hideIcon() {
            return !this.icon
        },
        borderColorStyle() {
            if (!this.hasLink) {
                return ''
            }

            const boxShadow = 'box-shadow: 0px 0px 10px 1px'

            if (this.color.startsWith('#')) {
                // 70% alpha
                var hexWithAlpha = this.color.substring(0, 7) + 'B2'
                return `${boxShadow} ${hexWithAlpha};`
            } else if (this.color.startsWith('rgb')) {
                var rgbSection = this.color.match(/rgba?\((\d+\s*,\s*\d+\s*,\s*\d+).+/)[1]
                return `${boxShadow} rgba(${rgbSection}, 0.7);`
            }

            return `${boxShadow} rgba(var(--vs-${this.color}), 0.3);`
        },
        color_x() {
            return _color.getColorString(this.color)
        },
        backgroundColor_x() {
            if (!this.hasLink) {
                return ''
            }
            return _color.getColorString(this.color, 0.2)
        },
        backgroundColorLight() {
            if (!this.hasLink) {
                return ''
            }
            return _color.getColorString(this.color, 0.08)
        }
    },
    methods: {
        followLink(e) {
            this.$emit('click', e)
            if (!this.hasLink) {
                return
            }
            if (this.beforeLink) {
                this.beforeLink()
            }
            this.$router.push(this.link)
        }
    }
}
</script>

<style lang="scss" scoped>
.stat-link {
    border-radius: 24px;
    overflow: hidden;
    transition: all 0.2s ease;
    .go-text {
        left: -5rem;
        transition: all 0.2s ease;
    }
    .icon-avatar {
        position: absolute;
        left: 0;
        transition: all 0.2s ease;
        .filler {
            width: 40rem;
            height: 30rem;
            left: -40rem;
            position: absolute;
        }
    }

    &:hover {
        cursor: pointer;

        .arrow-link {
            transform: translateX(0);
            display: flex;
            opacity: 1;
        }
        .icon-avatar {
            left: calc(100% - 3rem);
        }

        .go-text {
            left: 0.5rem;
        }
    }

    ::v-deep .rounded-icon {
        border-radius: 0% 50% 50% 0%;
    }

    &:not(:hover) {
        box-shadow: none !important;
    }
}

.arrow-link {
    transform: translateX(-15px);
    transition: all 0.25s ease;
    opacity: 0;
}

.stat-text {
    display: flex;
    align-items: center;
    flex-grow: 1;
    justify-content: space-between;
    padding-left: 0.5rem;
    padding-right: 0.75rem;
    white-space: nowrap;
}

.right {
    .icon-avatar {
        left: auto;
        right: 0;
        .filler {
            right: -40rem;
        }
    }
    .stat-text {
        margin-left: 0rem;
        padding-right: 0.5rem;
        padding-left: 0.75rem;
        text-align: right;
    }
}
</style>
